/**
 * Agent Streaming RPC definitions
 *
 * RPC definitions for agent command dispatch, data ingestion, and SSH bridging.
 * Command dispatch and SSH bridge are admin-only (AdminMiddleware).
 * Agent ingestion and streaming endpoints use agent secret auth (AgentSecretMiddleware).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OcAssessmentSummary } from "@fm/db/models/AgentInstallation";
import { SystemVitals } from "@fm/db/models/AgentInstallationStats";
import { InstallationLogLevel, InstallationLogSource } from "@fm/db/models/InstallationLog";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { AgentSecretMiddleware } from "../agent-secret.tags.ts";
import {
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  RemoteSupportNotEnabled,
  Unauthorized,
  ValkeyError,
} from "../errors.ts";

/**
 * Maximum number of log lines accepted in a single appendLogLines call.
 * Keeps insert latency bounded for high-volume producers (Mac bootstrap,
 * Hetzner provisioning script, management agent).
 *
 * @since 1.0.0
 */
export const MAX_APPEND_LOG_LINES = 200;

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Command types that can be dispatched to management agents.
 * @since 1.0.0
 */
export const AgentCommandType = Schema.Literal(
  "restart",
  "update",
  "config.get",
  "config.write",
  "ssh.open",
  "ssh.close",
  "agent.checkUpdate",
  "applyProfile",
  "uninstall",
  "backup.now",
  "devices.list",
  "devices.approve",
  "devices.reject",
  // CLA-992: OpenClaw core upgrade / operator-triggered rollback. Dispatched
  // to a canary box, run in the background behind clawd's apply semaphore, and
  // reported per-step through mgmt.reportMigrationEvent.
  "openclaw.upgrade",
  "openclaw.rollback",
  // Backup listing / restore. Only ever enqueued by the typed backups surface,
  // which checks the box's clawd version first — never by the generic
  // dispatch paths (see OperatorDispatchableCommandType).
  "backup.list",
  "backup.restore",
);
export type AgentCommandType = Schema.Schema.Type<typeof AgentCommandType>;

/**
 * Command types the generic operator dispatch paths
 * (`admin.agent.dispatchCommand` and `POST /internal/nodes/:nodeId/command`)
 * accept: {@link AgentCommandType} minus `backup.list` / `backup.restore`.
 * Those two are only enqueued behind the clawd-version gate — an old clawd
 * that receives an unknown type fails to decode it and its cursor never
 * advances past it (CLA-708), so the generic paths must never bypass the gate.
 *
 * @since 1.0.0
 */
export const OperatorDispatchableCommandType = AgentCommandType.pipe(
  Schema.pickLiteral(
    "restart",
    "update",
    "config.get",
    "config.write",
    "ssh.open",
    "ssh.close",
    "agent.checkUpdate",
    "applyProfile",
    "uninstall",
    "backup.now",
    "devices.list",
    "devices.approve",
    "devices.reject",
    "openclaw.upgrade",
    "openclaw.rollback",
  ),
);
export type OperatorDispatchableCommandType = Schema.Schema.Type<typeof OperatorDispatchableCommandType>;

/**
 * Payload for the `openclaw.upgrade` command. Names the version pair that
 * drives one upgrade: `targetVersion` is the OpenClaw calver to install and
 * `previousVersion` the currently-running calver clawd rolls back to on gate
 * failure.
 *
 * @since 1.0.0
 */
export const OpenclawUpgradePayload = Schema.Struct({
  targetVersion: Schema.String,
  previousVersion: Schema.String,
});
export type OpenclawUpgradePayload = Schema.Schema.Type<typeof OpenclawUpgradePayload>;

/**
 * Payload for the `openclaw.rollback` command. `previousVersion` is the
 * OpenClaw calver to reinstall.
 *
 * @since 1.0.0
 */
export const OpenclawRollbackPayload = Schema.Struct({
  previousVersion: Schema.String,
});
export type OpenclawRollbackPayload = Schema.Schema.Type<typeof OpenclawRollbackPayload>;

/**
 * Command payload for dispatch.
 * @since 1.0.0
 */
export const CommandPayload = Schema.Struct({
  type: AgentCommandType,
  payload: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.Unknown }), {
    default: () => ({}),
  }),
});
export type CommandPayload = Schema.Schema.Type<typeof CommandPayload>;

/**
 * Node metrics snapshot from the agent.
 * @since 1.0.0
 */
export const NodeMetricsPayload = Schema.Struct({
  cpu: Schema.Number,
  memory: Schema.Number,
  activeSessions: Schema.Number,
});
export type NodeMetricsPayload = Schema.Schema.Type<typeof NodeMetricsPayload>;

/**
 * Config snapshot from the agent.
 * @since 1.0.0
 */
export const ConfigSnapshotPayload = Schema.Struct({
  config: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  gitHash: Schema.String,
});
export type ConfigSnapshotPayload = Schema.Schema.Type<typeof ConfigSnapshotPayload>;

/**
 * Git log entry for config history.
 * @since 1.0.0
 */
export const GitLogEntry = Schema.Struct({
  hash: Schema.String,
  message: Schema.String,
  author: Schema.String,
  timestamp: Schema.String,
});
export type GitLogEntry = Schema.Schema.Type<typeof GitLogEntry>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for dispatching a command to a node.
 * @since 1.0.0
 */
export const CommandDispatchInput = Schema.Struct({
  nodeId: Schema.String,
  type: OperatorDispatchableCommandType,
  payload: Schema.optionalWith(Schema.Record({ key: Schema.String, value: Schema.Unknown }), {
    default: () => ({}),
  }),
});

/**
 * Tagged outcome of the most-recent UpdateChecker.checkOnce, carried
 * piggyback on heartbeat. The agent encodes this as a wire string so
 * the api can persist it without a Schema round-trip.
 *
 * @since 1.0.0
 */
export const HeartbeatLastUpdateOutcome = Schema.Literal(
  "up-to-date",
  "swapped",
  "paused",
  "downgrade-blocked",
  "rolled-back",
  "failed:sha256",
  "failed:download",
  // CLA-724: local filesystem / permission failure during the swap, distinct
  // from `failed:download` so a wrong install path is diagnosable instead of
  // masquerading as a network failure.
  "failed:filesystem",
  "failed:manifest-not-found",
  "failed:manifest-parse",
  "failed:network",
  // CLA-510: specific manifest-resolution failures, so `last_update_outcome`
  // says WHICH step 404'd instead of an opaque `manifest-not-found`. The bare
  // `manifest-not-found` above is retained for pre-CLA-510 agents / proxy 404s
  // whose body carries no reason.
  "failed:install-missing",
  "failed:pinned-release-missing",
  "failed:channel-missing",
  "failed:no-current-release",
  "failed:no-platform-asset",
);
export type HeartbeatLastUpdateOutcome = Schema.Schema.Type<typeof HeartbeatLastUpdateOutcome>;

/**
 * OpenClaw health snapshot carried by the heartbeat. Written by the
 * agent's health-probe runner (C3a) and sent verbatim on each tick by
 * the heartbeat loop (C3c). The field is *omitted* — not nulled — on
 * cold-boot heartbeats before the first probe has run, so the api can
 * distinguish "never probed" from "probed and not ok".
 *
 * @since 1.0.0
 */
export const HeartbeatOcHealth = Schema.Struct({
  status: Schema.Literal("ok", "degraded", "down"),
  reasonCode: Schema.NullOr(Schema.String),
  observedAt: Schema.String,
  /**
   * OpenClaw version parsed from the local `openclaw health --json` probe
   * (CLA-320). Optional/null: pre-CLA-320 agents omit it, and a failed probe
   * carries no version. Persisted to `agent_installations.oc_version` so admin
   * can surface the box's OpenClaw version alongside the clawd `agent_version`.
   */
  openclawVersion: Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null }),
  /**
   * The full `openclaw health --json` document (per-channel status, agent
   * availability, session-store summary) carried verbatim so the admin control
   * plane can surface failures — e.g. Discord zero-channels — that the
   * projected `{status, reasonCode, observedAt}` triple discards. `Schema.Unknown`
   * because the contents are opake to this repo and never parsed: a probe sends
   * the raw stdout string, or a `{ truncated, bytes }` marker above the size cap.
   * Optional: old clawd binaries and failed/cold-boot probes omit it entirely.
   */
  raw: Schema.optional(Schema.Unknown),
});
export type HeartbeatOcHealth = Schema.Schema.Type<typeof HeartbeatOcHealth>;

/**
 * Command-stream reader health carried by the heartbeat (CLA-708). Written by
 * the agent's command-stream loop and sent on every tick so a wedged reader —
 * a box that keeps heartbeating but has silently stopped consuming dispatched
 * commands after a transport error — is visible to the fleet instead of only
 * appearing as a WARN in the box's own journal.
 *
 * Optional/null: pre-CLA-708 agents omit it. `status: "failing"` means the
 * reader has errored past its retry threshold without consuming a command.
 *
 * @since 1.0.0
 */
export const HeartbeatCommandStream = Schema.Struct({
  status: Schema.Literal("ok", "failing"),
  consecutiveFailures: Schema.Int,
  lastErrorAt: Schema.NullOr(Schema.String),
});
export type HeartbeatCommandStream = Schema.Schema.Type<typeof HeartbeatCommandStream>;

/**
 * Input for agent heartbeat. The four versioning fields are optional
 * for backwards compatibility with pre-Phase-4 agents that don't yet
 * include them. `ocHealth` is also optional — pre-C3c agents never
 * send it, and post-C3c agents omit it during the cold-boot window.
 * `commandStream` is optional for the same reason — pre-CLA-708 agents
 * never send it.
 *
 * @since 1.0.0
 */
export const HeartbeatInput = Schema.Struct({
  nodeId: Schema.String,
  timestamp: Schema.String,
  agentVersion: Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null }),
  lastUpdateCheckAt: Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null }),
  lastUpdateOutcome: Schema.optionalWith(Schema.NullOr(HeartbeatLastUpdateOutcome), {
    default: () => null,
  }),
  updateTargetVersion: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
  ocHealth: Schema.optionalWith(Schema.NullOr(HeartbeatOcHealth), {
    default: () => null,
  }),
  commandStream: Schema.optionalWith(Schema.NullOr(HeartbeatCommandStream), {
    default: () => null,
  }),
  // CLA-775: the compact DoctorLoop assessment projection. Reuses the model's
  // exported `OcAssessmentSummary` since the wire and persisted shapes are
  // identical. Optional/null: pre-CLA-775 clawd binaries omit it and keep
  // heartbeating; a box that has not yet run its first assessment cycle omits
  // it during cold-boot.
  assessment: Schema.optionalWith(Schema.NullOr(OcAssessmentSummary), {
    default: () => null,
  }),
  // CLA-784: remote-support consent carried by the heartbeat — the ONLY
  // reporter of the consent record (the box's unit env file is the consent
  // store; sibling CLA-808 populates both fields from clawd's config).
  // Optional/null: pre-CLA-808 binaries omit both and keep heartbeating;
  // omission is a no-op on the persisted record.
  remoteSupportEnabled: Schema.optionalWith(Schema.NullOr(Schema.Boolean), {
    default: () => null,
  }),
  remoteSupportDisclosure: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
  // Box vitals from clawd's 30 s collector. Optional/null: older clawd binaries omit it.
  vitals: Schema.optionalWith(Schema.NullOr(SystemVitals), { default: () => null }),
});
export type HeartbeatInput = Schema.Schema.Type<typeof HeartbeatInput>;

/**
 * Input for agent log ingestion.
 * @since 1.0.0
 */
export const LogIngestInput = Schema.Struct({
  nodeId: Schema.String,
  lines: Schema.Array(Schema.String),
});

/**
 * Input for agent metrics ingestion.
 * @since 1.0.0
 */
export const MetricsIngestInput = Schema.Struct({
  nodeId: Schema.String,
  metrics: NodeMetricsPayload,
});

/**
 * Input for config snapshot storage.
 * @since 1.0.0
 */
export const ConfigSnapshotInput = Schema.Struct({
  nodeId: Schema.String,
  config: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  gitHash: Schema.String,
});

/**
 * Input for config history storage.
 * @since 1.0.0
 */
export const ConfigHistoryInput = Schema.Struct({
  nodeId: Schema.String,
  entries: Schema.Array(GitLogEntry),
});

/**
 * Input for reading commands from a node's stream.
 * Agent sends its last-seen command ID on connect to resume.
 * @since 1.0.0
 */
export const ReadCommandsInput = Schema.Struct({
  nodeId: Schema.String,
  lastSeenId: Schema.optionalWith(Schema.String, { default: () => "0" }),
});

/**
 * Input for acknowledging consumed commands (CLA-1000). The agent reports
 * the stream id of the last command it dispatched so the server persists
 * the ack cursor and `XTRIM MINID`s acknowledged entries away — a restart
 * then resumes from the ack instead of replaying the whole stream.
 * @since 1.0.0
 */
export const AckCommandsInput = Schema.Struct({
  nodeId: Schema.String,
  lastSeenId: Schema.String,
});
export type AckCommandsInput = Schema.Schema.Type<typeof AckCommandsInput>;

/**
 * Input for SSH session open/close.
 * @since 1.0.0
 */
export const SshSessionInput = Schema.Struct({
  nodeId: Schema.String,
  sessionId: Schema.String,
});

/**
 * A single log line emitted by an installer or management agent.
 * `ts` is an ISO-8601 timestamp produced by the originating client.
 *
 * @since 1.0.0
 */
export const AppendLogLine = Schema.Struct({
  source: InstallationLogSource,
  level: InstallationLogLevel,
  message: Schema.String,
  meta: Schema.optional(Schema.Unknown),
  ts: Schema.String,
});
export type AppendLogLine = Schema.Schema.Type<typeof AppendLogLine>;

/**
 * Input for the `admin.installs.appendLogLines` RPC. The agent secret
 * middleware authenticates the caller; the `installationId` is asserted
 * by the caller and stored as-is.
 *
 * @since 1.0.0
 */
export const AppendLogLinesInput = Schema.Struct({
  installationId: Schema.String,
  lines: Schema.Array(AppendLogLine),
});
export type AppendLogLinesInput = Schema.Schema.Type<typeof AppendLogLinesInput>;

/**
 * Returned by `admin.installs.appendLogLines` when the batch exceeds
 * the server-side cap (`MAX_APPEND_LOG_LINES`). The handler does not
 * truncate silently — callers are expected to chunk on their side.
 *
 * @since 1.0.0
 */
export class LogBatchTooLargeError extends Schema.TaggedError<LogBatchTooLargeError>()(
  "LogBatchTooLargeError",
  {
    received: Schema.Int,
    max: Schema.Int,
  },
) {}

/**
 * Acknowledgment for a successful append.
 *
 * @since 1.0.0
 */
export const AppendLogLinesResponse = Schema.Struct({
  ok: Schema.Boolean,
  inserted: Schema.Int,
});
export type AppendLogLinesResponse = Schema.Schema.Type<typeof AppendLogLinesResponse>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Command dispatch response.
 * @since 1.0.0
 */
export const CommandDispatchResponse = Schema.Struct({
  commandId: Schema.String,
  streamEntryId: Schema.NullOr(Schema.String),
});
export type CommandDispatchResponse = Schema.Schema.Type<typeof CommandDispatchResponse>;

/**
 * Ingestion acknowledgment.
 * @since 1.0.0
 */
export const IngestAck = Schema.Struct({
  ok: Schema.Boolean,
});

/**
 * Command entry from stream.
 * @since 1.0.0
 */
export const CommandEntry = Schema.Struct({
  streamId: Schema.String,
  commandId: Schema.String,
  type: AgentCommandType,
  payload: Schema.String,
  createdAt: Schema.String,
});
export type CommandEntry = Schema.Schema.Type<typeof CommandEntry>;

/**
 * Read commands response (used by non-streaming admin endpoint).
 * @since 1.0.0
 */
export const ReadCommandsResponse = Schema.Struct({
  commands: Schema.Array(CommandEntry),
});

/**
 * SSH session response.
 * @since 1.0.0
 */
export const SshSessionResponse = Schema.Struct({
  sessionId: Schema.String,
  commandId: Schema.String,
});
export type SshSessionResponse = Schema.Schema.Type<typeof SshSessionResponse>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Agent Stream RPC group
 *
 * Admin endpoints (dispatch, SSH bridge) require AdminMiddleware.
 * Agent endpoints (streaming, heartbeat, ingestion) require AgentSecretMiddleware.
 *
 * @since 1.0.0
 */
export class AgentStreamRpcs extends RpcGroup.make(
  // Admin: dispatch command to a node
  Rpc.make("admin.agent.dispatchCommand", {
    payload: CommandDispatchInput,
    success: CommandDispatchResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RemoteSupportNotEnabled,
      ValkeyError,
    ),
  }).middleware(AdminMiddleware),
  // Admin: read commands snapshot (non-streaming, admin-only)
  Rpc.make("admin.agent.readCommands", {
    payload: ReadCommandsInput,
    success: ReadCommandsResponse,
    error: Schema.Union(Unauthorized, Forbidden, ValkeyError),
  }).middleware(AdminMiddleware),
  // Agent: persistent streaming connection for command delivery via XREAD BLOCK
  Rpc.make("admin.agent.streamCommands", {
    payload: ReadCommandsInput,
    success: CommandEntry,
    error: Schema.Union(Unauthorized, ValkeyError),
    stream: true,
  }).middleware(AgentSecretMiddleware),
  // Agent: acknowledge consumed commands — persist the ack cursor + XTRIM
  // the stream so a restart never replays acknowledged commands (CLA-1000).
  Rpc.make("admin.agent.ackCommands", {
    payload: AckCommandsInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, ValkeyError),
  }).middleware(AgentSecretMiddleware),
  // Agent: heartbeat
  Rpc.make("admin.agent.heartbeat", {
    payload: HeartbeatInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, ValkeyError),
  }).middleware(AgentSecretMiddleware),
  // Agent: log ingestion
  Rpc.make("admin.agent.ingestLogs", {
    payload: LogIngestInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, ValkeyError),
  }).middleware(AgentSecretMiddleware),
  // Agent: metrics ingestion
  Rpc.make("admin.agent.ingestMetrics", {
    payload: MetricsIngestInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, ValkeyError),
  }).middleware(AgentSecretMiddleware),
  // Agent: config snapshot (stored in DB)
  Rpc.make("admin.agent.storeConfigSnapshot", {
    payload: ConfigSnapshotInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AgentSecretMiddleware),
  // Agent: config history (stored in DB)
  Rpc.make("admin.agent.storeConfigHistory", {
    payload: ConfigHistoryInput,
    success: IngestAck,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AgentSecretMiddleware),
  // SSH bridge: open session (admin-only)
  Rpc.make("admin.agent.sshOpen", {
    payload: SshSessionInput,
    success: SshSessionResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RemoteSupportNotEnabled,
      ValkeyError,
    ),
  }).middleware(AdminMiddleware),
  // SSH bridge: close session (admin-only)
  Rpc.make("admin.agent.sshClose", {
    payload: SshSessionInput,
    success: IngestAck,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RemoteSupportNotEnabled,
      ValkeyError,
    ),
  }).middleware(AdminMiddleware),
  // Agent / installer: append runtime log lines for an installation
  Rpc.make("admin.installs.appendLogLines", {
    payload: AppendLogLinesInput,
    success: AppendLogLinesResponse,
    error: Schema.Union(Unauthorized, DatabaseError, LogBatchTooLargeError),
  }).middleware(AgentSecretMiddleware),
) {}
